import type { TextTemplateCategory, TextTemplateItem, TextTemplateStyle } from './textTemplateCatalog';

const salonBanner = (
  id: string,
  label: string,
  fontFamily: string,
  fontSize: number,
  width: number,
  extra: TextTemplateStyle & {
    themeAdaptiveFill?: boolean;
    previewScale?: number;
    keywords?: string[];
  } = {},
): TextTemplateItem => {
  const { themeAdaptiveFill, previewScale, keywords, ...styleExtra } = extra;
  return {
    id,
    label,
    themeAdaptiveFill,
    previewScale: previewScale ?? (fontSize >= 56 ? 2.25 : 2),
    keywords: [
      'salon',
      'spa',
      'clinic',
      'beauty',
      'wellness',
      'appointment',
      'emoji',
      label,
      ...(keywords ?? []),
    ],
    style: {
      text: label,
      fontFamily,
      fontSize,
      width,
      height: Math.round(fontSize * 1.15),
      align: 'center',
      letterSpacing: 0,
      lineHeight: 1.05,
      x: Math.max(50, Math.round((520 - width) / 2)),
      y: 95,
      ...styleExtra,
    },
  };
};

const salonTag = (
  id: string,
  label: string,
  fontFamily: string,
  fontSize: number,
  width: number,
  extra: TextTemplateStyle & {
    themeAdaptiveFill?: boolean;
    previewScale?: number;
    keywords?: string[];
  } = {},
): TextTemplateItem =>
  salonBanner(id, label, fontFamily, fontSize, width, {
    fontStyle: 'bold',
    letterSpacing: fontSize <= 22 ? 2.5 : 1.5,
    previewScale: extra.previewScale ?? (fontSize <= 24 ? 2.65 : 2.35),
    keywords: ['tag', 'badge', 'sticker', ...(extra.keywords ?? [])],
    ...extra,
  });

/**
 * Salon, spa, and aesthetic clinic presets (primary Respark vertical).
 */
export const SALON_SPA_CLINIC_TEXT_TEMPLATE_CATEGORIES: TextTemplateCategory[] = [
  {
    id: 'salon-emoji',
    title: 'Emoji highlights',
    description: 'Popular icons with salon & spa copy — same as paste from Google',
    templates: [
      salonBanner('emoji-book-now', '📅 BOOK NOW', 'Bebas Neue', 52, 340, {
        fill: '#f8558e',
        letterSpacing: 2,
        keywords: ['booking'],
      }),
      salonBanner('emoji-spa-day', '✨ SPA DAY ✨', 'GreatVibes', 48, 360, {
        fill: '#a855f7',
        previewScale: 1.85,
      }),
      salonBanner('emoji-massage', '💆 MASSAGE', 'Montserrat', 44, 320, {
        fontStyle: 'bold',
        fill: '#6366f1',
      }),
      salonBanner('emoji-nails', '💅 NAILS & BEAUTY', 'Anton', 42, 380, {
        fill: '#ec4899',
        previewScale: 1.9,
      }),
      salonBanner('emoji-hair', '✂️ HAIR STUDIO', 'Bebas Neue', 50, 360, {
        fill: '#111111',
        letterSpacing: 2,
      }),
      salonBanner('emoji-gift', '🎁 GIFT CARDS', 'Pacifico', 44, 340, {
        fill: '#f8558e',
        previewScale: 1.75,
      }),
      salonBanner('emoji-new-client', '🎉 NEW CLIENTS', 'Lilita One', 46, 360, {
        fill: '#22c55e',
      }),
      salonBanner('emoji-love', 'THANK YOU ❤️', 'Dancing Script', 44, 360, {
        fill: '#f8558e',
        previewScale: 1.75,
      }),
      salonBanner('emoji-relax', 'RELAX & RENEW 🌿', 'Cormorant Garamond', 34, 400, {
        fontStyle: 'italic',
        fill: '#14b8a6',
        previewScale: 1.7,
      }),
      salonBanner('emoji-couples', 'COUPLES SPA 💑', 'Parisienne', 40, 360, {
        fill: '#d63384',
        previewScale: 1.75,
      }),
      salonBanner('emoji-bridal', 'BRIDAL GLAM 💐', 'Playfair Display', 38, 360, {
        fontStyle: 'italic',
        fill: '#f8558e',
        previewScale: 1.8,
      }),
      salonBanner('emoji-vip', 'VIP 👑', 'Anton', 56, 220, {
        fill: '#fbbf24',
        letterSpacing: 2,
      }),
      salonBanner('emoji-glow', 'GLOW UP ✨', 'Bebas Neue', 54, 300, {
        fill: '#ffffff',
        letterSpacing: 3,
      }),
      salonBanner('emoji-sale', '🔥 FLASH SALE', 'Archivo Black', 48, 340, {
        fill: '#ef4444',
      }),
      salonBanner('emoji-self-care', 'SELF CARE 💖', 'Satisfy', 42, 320, {
        fill: '#ec4899',
        previewScale: 1.8,
      }),
      salonBanner('emoji-call', '📞 CALL TO BOOK', 'Barlow Condensed', 38, 400, {
        fontStyle: 'bold',
        fill: '#0ea5e9',
        previewScale: 1.75,
      }),
      salonBanner('emoji-open', 'OPEN TODAY ✅', 'Oswald', 46, 340, {
        letterSpacing: 2,
        fill: '#22c55e',
      }),
      salonBanner('emoji-pamper', 'PAMPER YOURSELF 🧖', 'GreatVibes', 40, 400, {
        fill: '#14b8a6',
        previewScale: 1.75,
      }),
      salonBanner('emoji-facial', '✨ FACIAL SPECIAL', 'Montserrat', 36, 400, {
        fontStyle: 'bold',
        fill: '#a855f7',
        previewScale: 1.75,
      }),
    ],
  },
  {
    id: 'salon-booking',
    title: 'Book & visit',
    description: 'Appointments, walk-ins, and contact CTAs',
    templates: [
      salonBanner('book-apt', '📅 BOOK APPOINTMENT', 'Bebas Neue', 50, 440, {
        fill: '#f8558e',
        letterSpacing: 2,
        keywords: ['booking', 'cta'],
      }),
      salonBanner('book-online', '💻 BOOK ONLINE', 'Anton', 52, 360, {
        fill: '#ffffff',
        letterSpacing: 2,
      }),
      salonBanner('call-book', '📞 CALL TO BOOK', 'Barlow Condensed', 44, 380, {
        fontStyle: 'bold',
        fill: '#14b8a6',
      }),
      salonBanner('schedule-today', 'SCHEDULE TODAY', 'Montserrat', 40, 360, {
        fontStyle: 'bold',
        fill: '#a855f7',
      }),
      salonBanner('walk-ins-welcome', 'WALK-INS WELCOME', 'Roboto Condensed', 34, 400, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        previewScale: 1.75,
      }),
      salonBanner('same-day-apt', 'SAME-DAY APPOINTMENTS', 'FiraSansCondensed', 32, 420, {
        fontStyle: 'bold',
        fill: '#0ea5e9',
        previewScale: 1.7,
      }),
      salonBanner('open-today', 'OPEN TODAY ✅', 'Archivo Black', 50, 320, {
        fill: '#22c55e',
      }),
      salonBanner('hours', 'TUE–SAT 9AM–7PM', 'Roboto Condensed', 26, 400, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        previewScale: 1.6,
        keywords: ['hours', 'open'],
      }),
      salonBanner('text-book', '💬 TEXT TO BOOK', 'Outfit', 40, 340, {
        fontStyle: 'bold',
        fill: '#f8558e',
      }),
      salonBanner('reserve', 'RESERVE YOUR SPOT', 'League Spartan', 38, 400, {
        fontStyle: 'bold',
        fill: '#6366f1',
        previewScale: 1.75,
      }),
    ],
  },
  {
    id: 'salon-hair',
    title: 'Hair salon',
    description: 'Cuts, color, styling, and bridal hair',
    templates: [
      salonBanner('hair-cut', '✂️ HAIRCUT & STYLE', 'Bebas Neue', 50, 420, {
        fill: '#111111',
        letterSpacing: 2,
      }),
      salonBanner('color-highlights', 'COLOR & HIGHLIGHTS', 'Oswald', 44, 400, {
        letterSpacing: 3,
        fill: '#f8558e',
      }),
      salonBanner('blowout', 'BLOWOUT SPECIAL', 'Anton', 48, 360, {
        fill: '#fbbf24',
      }),
      salonBanner('balayage', 'BALAYAGE', 'Archivo Black', 54, 280, {
        fill: '#d97706',
        letterSpacing: 4,
      }),
      salonBanner('keratin', 'KERATIN TREATMENT', 'Barlow Condensed', 38, 400, {
        fontStyle: 'bold',
        fill: '#ffffff',
        previewScale: 1.8,
      }),
      salonBanner('bridal-hair', 'BRIDAL HAIR 💐', 'Playfair Display', 42, 380, {
        fontStyle: 'italic',
        fill: '#f8558e',
        previewScale: 1.8,
        keywords: ['wedding', 'bride'],
      }),
      salonBanner('mens-groom', "MEN'S GROOMING", 'Montserrat', 38, 360, {
        fontStyle: 'bold',
        fill: '#1e3a5f',
        previewScale: 1.75,
      }),
      salonBanner('beard-trim', 'BEARD TRIM', 'Bebas Neue', 52, 300, {
        fill: '#ffffff',
        letterSpacing: 2,
      }),
      salonBanner('kids-cut', 'KIDS HAIRCUT', 'Lilita One', 46, 320, {
        fill: '#0ea5e9',
      }),
      salonBanner('hair-extensions', 'HAIR EXTENSIONS', 'League Spartan', 40, 380, {
        fontStyle: 'bold',
        letterSpacing: 2,
        fill: '#ec4899',
        previewScale: 1.8,
      }),
    ],
  },
  {
    id: 'spa-wellness',
    title: 'Spa & wellness',
    description: 'Relaxation, massage, and self-care messaging',
    templates: [
      salonBanner('spa-day', '✨ SPA DAY', 'GreatVibes', 56, 300, {
        fill: '#a855f7',
        previewScale: 1.9,
      }),
      salonBanner('relax-rejuvenate', 'RELAX & REJUVENATE', 'Cormorant Garamond', 36, 420, {
        fontStyle: 'italic',
        fill: '#14b8a6',
        previewScale: 1.75,
      }),
      salonBanner('massage', '💆 MASSAGE THERAPY', 'Playfair Display', 40, 420, {
        fontStyle: 'italic',
        fill: '#6366f1',
        previewScale: 1.8,
      }),
      salonBanner('deep-tissue', 'DEEP TISSUE', 'Oswald', 50, 300, {
        letterSpacing: 3,
        fill: '#0f766e',
      }),
      salonBanner('hot-stone', 'HOT STONE', 'Bebas Neue', 54, 280, {
        fill: '#dc2626',
        letterSpacing: 2,
      }),
      salonBanner('aromatherapy', 'AROMATHERAPY', 'PetitFormalScript', 40, 360, {
        fill: '#15803d',
        previewScale: 1.75,
      }),
      salonBanner('couples-massage', 'COUPLES MASSAGE 💑', 'Dancing Script', 42, 440, {
        fill: '#f8558e',
        previewScale: 1.75,
        keywords: ['valentine', 'romantic'],
      }),
      salonBanner('treat-yourself', 'TREAT YOURSELF 💖', 'Parisienne', 44, 400, {
        fill: '#d63384',
        previewScale: 1.8,
      }),
      salonBanner('self-care', 'SELF CARE ✨', 'Satisfy', 48, 320, {
        fill: '#ec4899',
        previewScale: 1.85,
      }),
      salonBanner('wellness-reset', 'WELLNESS RESET', 'Montserrat', 40, 360, {
        fontStyle: 'bold',
        fill: '#14b8a6',
      }),
      salonBanner('combo-spa', 'SPA PACKAGE\nMASSAGE + FACIAL', 'Montserrat', 32, 400, {
        fontStyle: 'bold',
        lineHeight: 1.12,
        height: 78,
        fill: '#a855f7',
        previewScale: 1.55,
        keywords: ['package', 'combo'],
      }),
    ],
  },
  {
    id: 'salon-nails-beauty',
    title: 'Nails & beauty bar',
    description: 'Nails, lashes, brows, and makeup',
    templates: [
      salonBanner('manicure-pedi', '💅 MANICURE & PEDICURE', 'Bebas Neue', 46, 460, {
        fill: '#f8558e',
        letterSpacing: 1,
        previewScale: 2,
      }),
      salonBanner('gel-nails', '💅 GEL NAILS', 'Anton', 52, 320, {
        fill: '#ec4899',
      }),
      salonBanner('nail-art', 'NAIL ART', 'Lilita One', 50, 260, {
        fill: '#a855f7',
      }),
      salonBanner('lash-extensions', 'LASH EXTENSIONS ✨', 'Barlow Condensed', 40, 420, {
        fontStyle: 'bold',
        fill: '#111111',
        previewScale: 1.8,
      }),
      salonBanner('brow-lamination', 'BROW LAMINATION', 'Roboto Condensed', 34, 400, {
        fontStyle: 'bold',
        fill: '#d97706',
        previewScale: 1.75,
      }),
      salonBanner('microblading', 'MICROBLADING', 'Archivo Black', 48, 360, {
        fill: '#78350f',
        letterSpacing: 2,
      }),
      salonBanner('makeup', 'MAKEUP SERVICES', 'Playfair Display', 38, 380, {
        fontStyle: 'italic',
        fill: '#f8558e',
        previewScale: 1.8,
      }),
      salonBanner('waxing', 'WAXING', 'Bebas Neue', 58, 240, {
        fill: '#ffffff',
        letterSpacing: 4,
      }),
      salonBanner('brow-wax', 'BROW WAX & TINT', 'Montserrat', 36, 360, {
        fontStyle: 'bold',
        fill: '#6366f1',
        previewScale: 1.75,
      }),
      salonBanner('glam', 'FULL GLAM', 'GreatVibes', 50, 280, {
        fill: '#fbbf24',
        previewScale: 1.9,
      }),
    ],
  },
  {
    id: 'clinic-aesthetics',
    title: 'Clinic & aesthetics',
    description: 'Med spa, skin clinic, and treatment promos',
    templates: [
      salonBanner('facial', 'FACIAL TREATMENT', 'Cormorant Garamond', 38, 400, {
        fontStyle: 'italic',
        fill: '#f8558e',
        previewScale: 1.8,
      }),
      salonBanner('hydrafacial', '✨ HYDRAFACIAL', 'Bebas Neue', 52, 380, {
        fill: '#0ea5e9',
        letterSpacing: 2,
        keywords: ['skin', 'med spa'],
      }),
      salonBanner('chemical-peel', 'CHEMICAL PEEL', 'Montserrat', 40, 360, {
        fontStyle: 'bold',
        fill: '#a855f7',
      }),
      salonBanner('microneedling', 'MICRONEEDLING', 'Barlow Condensed', 38, 380, {
        fontStyle: 'bold',
        fill: '#6366f1',
        previewScale: 1.8,
      }),
      salonBanner('botox', 'BOTOX & FILLERS', 'Oswald', 44, 380, {
        letterSpacing: 2,
        fill: '#111111',
        keywords: ['med spa', 'injectable'],
      }),
      salonBanner('laser-hair', 'LASER HAIR REMOVAL', 'Roboto Condensed', 32, 420, {
        fontStyle: 'bold',
        fill: '#7c3aed',
        previewScale: 1.7,
      }),
      salonBanner('skin-rejuvenation', 'SKIN REJUVENATION', 'Playfair Display', 34, 400, {
        fontStyle: 'italic',
        themeAdaptiveFill: true,
        previewScale: 1.75,
      }),
      salonBanner('acne-treatment', 'ACNE TREATMENT', 'League Spartan', 40, 360, {
        fontStyle: 'bold',
        fill: '#14b8a6',
      }),
      salonBanner('anti-aging', 'ANTI-AGING', 'Archivo Black', 50, 320, {
        fill: '#fbbf24',
        letterSpacing: 2,
      }),
      salonBanner('free-consult', 'FREE CONSULTATION ✨', 'Anton', 46, 440, {
        fill: '#22c55e',
        previewScale: 1.9,
        keywords: ['clinic', 'consult'],
      }),
      salonBanner('licensed-provider', 'LICENSED PROVIDER', 'Roboto Condensed', 28, 400, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        previewScale: 1.65,
        keywords: ['trust', 'medical'],
      }),
    ],
  },
  {
    id: 'salon-packages',
    title: 'Packages & gifts',
    description: 'Bundles, memberships, and gift certificates',
    templates: [
      salonBanner('spa-package', 'SPA PACKAGE', 'Bebas Neue', 52, 320, {
        fill: '#a855f7',
        letterSpacing: 2,
      }),
      salonBanner('bridal-package', 'BRIDAL PACKAGE', 'Playfair Display', 40, 380, {
        fontStyle: 'italic',
        fill: '#f8558e',
        previewScale: 1.8,
      }),
      salonBanner('couples-package', 'COUPLES PACKAGE', 'Dancing Script', 40, 400, {
        fill: '#ec4899',
        previewScale: 1.75,
      }),
      salonBanner('monthly-membership', 'MONTHLY MEMBERSHIP', 'Montserrat', 34, 400, {
        fontStyle: 'bold',
        fill: '#6366f1',
        previewScale: 1.75,
      }),
      salonBanner('vip-member', 'VIP MEMBER 👑', 'Anton', 54, 340, {
        fill: '#fbbf24',
        letterSpacing: 2,
      }),
      salonBanner('gift-certificate', '🎁 GIFT CERTIFICATES', 'GreatVibes', 42, 440, {
        fill: '#f8558e',
        previewScale: 1.8,
        keywords: ['gift card', 'holiday'],
      }),
      salonBanner('mother-daughter', 'MOTHER & DAUGHTER DAY', 'Parisienne', 36, 420, {
        fill: '#d63384',
        previewScale: 1.65,
        keywords: ['mothers day'],
      }),
      salonBanner('pamper-package', 'PAMPER PACKAGE', 'Pacifico', 40, 360, {
        fill: '#14b8a6',
        previewScale: 1.75,
      }),
      salonBanner('combo-save', 'PACKAGE & SAVE', 'Archivo Black', 48, 340, {
        fill: '#22c55e',
        letterSpacing: 2,
      }),
    ],
  },
  {
    id: 'salon-promos',
    title: 'Salon promos & offers',
    description: 'New client deals, referrals, and seasonal spa offers',
    templates: [
      salonBanner('new-client', '🎉 NEW CLIENT SPECIAL', 'Bebas Neue', 46, 440, {
        fill: '#f8558e',
        letterSpacing: 1,
        previewScale: 2,
      }),
      salonBanner('first-visit', '🎉 FIRST VISIT 20% OFF', 'Anton', 40, 460, {
        fill: '#22c55e',
        previewScale: 1.9,
      }),
      salonBanner('refer-friend-salon', 'REFER A FRIEND 💖', 'Barlow Condensed', 42, 400, {
        fontStyle: 'bold',
        fill: '#a855f7',
      }),
      salonBanner('birthday', '🎂 BIRTHDAY TREAT', 'GreatVibes', 48, 360, {
        fill: '#ec4899',
        previewScale: 1.85,
      }),
      salonBanner('happy-hour-spa', 'SPA HAPPY HOUR', 'Lilita One', 46, 360, {
        fill: '#f97316',
      }),
      salonBanner('midweek', 'MIDWEEK SPECIAL', 'Oswald', 46, 360, {
        letterSpacing: 3,
        fill: '#0ea5e9',
      }),
      salonBanner('flash-facial', 'FLASH FACIAL SALE', 'Montserrat', 36, 400, {
        fontStyle: 'bold',
        fill: '#ef4444',
        previewScale: 1.75,
      }),
      salonBanner('student-beauty', 'STUDENT DISCOUNT', 'Roboto Condensed', 34, 360, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        previewScale: 1.7,
      }),
      salonBanner('combo-mothers', "MOTHER'S DAY SPA\n20% OFF PACKAGES", 'Montserrat', 30, 420, {
        fontStyle: 'bold',
        lineHeight: 1.12,
        height: 76,
        fill: '#f8558e',
        previewScale: 1.55,
        keywords: ['seasonal', 'mothers day'],
      }),
      salonBanner('holiday-glow', 'HOLIDAY GLOW UP', 'Bebas Neue', 50, 380, {
        fill: '#fbbf24',
        letterSpacing: 2,
      }),
    ],
  },
  {
    id: 'salon-trust',
    title: 'Trust & care',
    description: 'Safety, expertise, and client care lines',
    templates: [
      salonBanner('sanitized', 'SANITIZED & SAFE ✅', 'Montserrat', 36, 420, {
        fontStyle: 'bold',
        fill: '#22c55e',
        previewScale: 1.75,
        keywords: ['hygiene', 'clean'],
      }),
      salonBanner('licensed-esthetician', 'LICENSED ESTHETICIAN', 'Roboto Condensed', 28, 420, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        previewScale: 1.65,
      }),
      salonBanner('experienced-team', 'EXPERIENCED TEAM', 'Barlow Condensed', 36, 380, {
        fontStyle: 'bold',
        fill: '#2563eb',
        previewScale: 1.75,
      }),
      salonBanner('premium-products', 'PREMIUM PRODUCTS', 'DM Serif Display', 34, 400, {
        themeAdaptiveFill: true,
        previewScale: 1.75,
      }),
      salonBanner('your-salon-name', 'YOUR SALON NAME', 'Bebas Neue', 54, 400, {
        fill: '#f8558e',
        letterSpacing: 3,
        keywords: ['brand', 'logo'],
      }),
      salonBanner('tagline-spa', 'Escape. Relax. Renew. ✨', 'Cormorant Garamond', 32, 440, {
        fontStyle: 'italic',
        fill: '#14b8a6',
        previewScale: 1.65,
        keywords: ['tagline'],
      }),
      salonBanner('since-year', 'SERVING YOU SINCE 2015', 'Roboto Condensed', 24, 400, {
        themeAdaptiveFill: true,
        letterSpacing: 2,
        previewScale: 1.55,
      }),
      salonBanner('award-salon', 'AWARD-WINNING SALON', 'Playfair Display', 32, 420, {
        fontStyle: 'italic',
        fill: '#fbbf24',
        previewScale: 1.7,
      }),
    ],
  },
  {
    id: 'salon-tags',
    title: 'Salon tags & stickers',
    description: 'Small badges for services and offers',
    templates: [
      salonTag('tag-facial', '✨ FACIAL', 'Anton', 28, 180, { fill: '#f8558e' }),
      salonTag('tag-massage', '💆 MASSAGE', 'Bebas Neue', 26, 200, { fill: '#6366f1' }),
      salonTag('tag-nails', '💅 NAILS', 'Archivo Black', 30, 160, { fill: '#ec4899' }),
      salonTag('tag-wax', 'WAX', 'Anton', 32, 120, { fill: '#d97706' }),
      salonTag('tag-lashes', 'LASHES', 'Barlow Condensed', 20, 160, {
        fill: '#111111',
        previewScale: 2.75,
      }),
      salonTag('tag-brows', 'BROWS', 'Roboto Condensed', 20, 150, {
        fill: '#a855f7',
        previewScale: 2.8,
      }),
      salonTag('tag-spa', '✨ SPA', 'Bebas Neue', 32, 130, { fill: '#14b8a6' }),
      salonTag('tag-new-client', '🎉 NEW', 'Montserrat', 16, 160, {
        fontStyle: 'bold',
        fill: '#22c55e',
        previewScale: 2.85,
      }),
      salonTag('tag-gift', '🎁 GIFT', 'Oswald', 18, 160, {
        fill: '#fbbf24',
        previewScale: 2.8,
      }),
      salonTag('tag-members', 'MEMBERS', 'League Spartan', 18, 170, {
        fill: '#6366f1',
        previewScale: 2.8,
      }),
      salonTag('tag-organic-spa', 'ORGANIC', 'Montserrat', 18, 160, {
        fill: '#15803d',
        previewScale: 2.75,
        keywords: ['eco'],
      }),
      salonTag('tag-cruelty', 'CRUELTY FREE', 'Outfit', 14, 220, {
        fontStyle: 'bold',
        fill: '#15803d',
        previewScale: 2.9,
      }),
    ],
  },
];
